import { useEffect, useMemo } from 'react'
import { FormikProvider, useField, useFormik } from 'formik'

import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { selectAdminOrUserGroupedFinancialAccountOptions } from '@/selectors/financeSelectors'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { compareArrays } from '@/features/admin/documents/helpers'
import {
  getFieldNames,
  makeNumberArraySchema,
  makeReqStringSchema,
} from '@/base'

import { Button, Dialog, Form, Text } from '@/ui'
import {
  GroupedDropdown,
  type GroupedDropdownProps,
} from '@/components/GroupedDropdown'
import { TypographyStyles } from '@/styles'

import { UserDocument, userUpdateStatement } from '../lib/userDocuments.slice'

interface EditStatementDialogProps {
  document: UserDocument
  isOpen: boolean
  onClose: () => void
}

const statementMonthSchema = makeReqStringSchema({ field: 'statement month' })

const accountIdsSchema = makeNumberArraySchema({ field: 'account' })

const AccountsDropdownField = ({
  name,
  options,
}: {
  name: string
  options: GroupedDropdownProps<number | string>['options']
}) => {
  const [field, , helpers] = useField<number[]>(name)
  return (
    <GroupedDropdown
      options={options}
      value={field.value}
      onChange={(value) =>
        helpers.setValue(
          value.filter((id): id is number => typeof id === 'number')
        )
      }
      placeholder="Select accounts"
    />
  )
}

export const EditStatementDialog = ({
  document,
  isOpen,
  onClose,
}: EditStatementDialogProps) => {
  const dispatch = useAppDispatch()
  const user = useReselector(getCurrentUser)
  const financialAccountOptions = useReselector(
    selectAdminOrUserGroupedFinancialAccountOptions,
    user?.id
  )

  const initialValues = useMemo(
    () => ({
      statementMonth: document.statement?.statementMonth ?? '',
      accountIds:
        document.statement?.statementFinancialAccounts?.map(
          (account) => account.financialAccountId
        ) ?? [],
    }),
    [document.statement]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { newVals, removedVals } = compareArrays(
        initialValues.accountIds,
        values.accountIds
      )

      const monthChanged =
        values.statementMonth !== initialValues.statementMonth
      const accountsChanged = newVals.length > 0 || removedVals.length > 0

      if (!monthChanged && !accountsChanged) {
        onClose()
        return
      }

      const result = await dispatch(
        userUpdateStatement(document.id, {
          ...(monthChanged && {
            statementMonth: values.statementMonth,
            statement: { statementMonth: values.statementMonth },
          }),
          ...(accountsChanged && {
            statementFinancialAccounts: {
              accountsToCreate: newVals,
              accountsToDelete: removedVals,
            },
          }),
        })
      )
      if (result) {
        onClose()
      }
    },
  })

  const fieldNames = getFieldNames(formik)
  const { submitForm, isSubmitting, resetForm } = formik

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchFinancialAccountsIfNeeded())
      resetForm({ values: initialValues })
    }
  }, [dispatch, isOpen, initialValues, resetForm])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content onDismiss={onClose} size="sm">
        <Dialog.Title>Edit statement</Dialog.Title>
        <Text variant={TypographyStyles.BODY_SM}>
          Update the statement month or assigned accounts for{' '}
          <b>{document.fileDescription}</b>.
        </Text>

        <FormikProvider value={formik}>
          <div className="flex flex-col gap-4">
            <Form.DateInput
              name={fieldNames.statementMonth}
              label="Statement month"
              placeholder="Month"
              dateFormat={DATE_FORMATS_LUXON.YEAR_MONTH}
              showMonthYearPicker
              schema={statementMonthSchema}
            />

            <Form.Field
              name={fieldNames.accountIds}
              label="Financial accounts"
              schema={accountIdsSchema}
            >
              <div>
                <AccountsDropdownField
                  name={fieldNames.accountIds}
                  options={financialAccountOptions}
                />
              </div>
            </Form.Field>
          </div>

          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              variant="primary"
              onClick={submitForm}
              disabled={isSubmitting}
            >
              Save
            </Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
